import { jwtDecode } from "jwt-decode"
import { toast } from "react-toastify"

import { getAuthUserId } from "./tokens"
import { Award, Circle } from "react-feather"
import { getUserData as getUserDataV2 } from "./authV2/tokenManager"
import { toRouteSlug } from "./routeSlug"

export const loadScriptByURL = (id, url, callback) => {
  const isScriptExist = document.getElementById(id)

  if (!isScriptExist) {
    const script = document.createElement("script")
    script.type = "text/javascript"
    script.src = url
    script.id = id
    script.onload = function () {
      if (callback) callback()
    }
    document.body.appendChild(script)
  }

  if (isScriptExist && callback) callback()
}

export const getMediaType = (mms_type) => {
  if (mms_type === "image") {
    return "photoMms"
  } else if (mms_type === "video") {
    return "videoMms"
  } else if (mms_type === "audio") {
    return "audioMms"
  } else {
    return "text"
  }
}

export const dataURLtoFile = (dataurl, filename) => {
  const arr = dataurl.split(","),
    mime = arr[0].match(/:(.*?);/)[1],
    bstr = atob(arr[1]),
    n = bstr.length,
    u8arr = new Uint8Array(n)
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  return new File([u8arr], filename, { type: mime })
}

export const getMonth = (month) => {
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
  ]
  return monthNames[month]
}

export const jsonParse = function (str) {
  try {
    return JSON.parse(str)
  } catch (e) {
    return {}
  }
}


export const isEmpty = (val) => {
  if (val === undefined || val === null || val === "" || val === 0 || val?.length === 0) {
    return true
  }
  if (typeof val === "object") {
    return Object.keys(val).length === 0 && val.constructor === Object
  }
  return false
}

export const removeTokenData = () => {
  if (typeof Storage !== "undefined") {
    sessionStorage.removeItem("profile")
    localStorage.removeItem("lsprofile")
    if (typeof document !== "undefined") {
      document.cookie = "a=0; path=/"
    }
  }
}

export const isAccessAllowed = (vertical, project, module) => {
  const legacyToken = localStorage.getItem("accessToken")
  const v2UserData = getUserDataV2()

  if (isEmpty(legacyToken) && !v2UserData) {
    localStorage.clear()
    window.location.href = "/"
    return false
  }

  // Slugged on both sides of every comparison below. The URL carries the
  // compact form ("productiontoolkit") while tokens grant the spoken one
  // ("Production Toolkit"); comparing them raw denies access to every project
  // or module whose name has a space in it — and a stale link still carrying
  // "%20" wouldn't match the token even after lowercasing.
  const normalizedProject = toRouteSlug(project)
  const normalizedModule = toRouteSlug(module)

  if (v2UserData?.access && Array.isArray(v2UserData.access)) {
    const projects = v2UserData.access

    const projectEntry = projects.find((p) => toRouteSlug(p.project) === normalizedProject)

    if (projectEntry) {
      if (!Array.isArray(projectEntry.modules) || projectEntry.modules.length === 0) {
        return true
      }

      const hasModule = projectEntry.modules.some(
        (m) => toRouteSlug(m.module_name) === normalizedModule
      )

      if (hasModule) {
        return true
      }
    }
  }

  if (!isEmpty(legacyToken)) {
    try {
      const decoded = jwtDecode(legacyToken)
      const access = decoded?.userData?.access || []

      for (let i = 0; i < access.length; i++) {
        if ((access[i].title || "").toLowerCase() === (vertical || "").toLowerCase()) {
          const projectsChildren = access[i].children || []
          for (let j = 0; j < projectsChildren.length; j++) {
            if (toRouteSlug(projectsChildren[j].id) === normalizedProject) {
              const modulesChildren = projectsChildren[j].children || []
              for (let k = 0; k < modulesChildren.length; k++) {
                if (toRouteSlug(modulesChildren[k].id) === normalizedModule) {
                  return true
                }
              }
            }
          }
        }
      }
    } catch (error) {
    }
  }

  return false
}

export const isUserLoggedIn = () => {
  // Check for both old and new token storage keys
  const oldToken = localStorage.getItem("accessToken")
  const newToken = localStorage.getItem("accessTokenV2")
  const isUserLogin = oldToken || newToken
  return isEmpty(isUserLogin) ? false : isUserLogin
  // return localStorage.getItem('userData') && localStorage.getItem(useJwt.jwtConfig.storageTokenKeyName)
}

const iconMapping = {
  Award: <Award size={18} />,
  Circle: <Circle size={15} />,
  // Add other icons as needed
  // Default icon
  default: ""
}
export const convertData = (data, keyPrefix = "") => {

  if (isEmpty(data)) {
    return;
  }
  return data?.map((item, index) => {
    const key = `${keyPrefix}${index + 1}`
    const newItem = {
      key: item.title,
      icon: iconMapping[item.icon] || iconMapping.default, // Assuming a static icon for simplicity
      label: (
        <a href={`#${item.navLink}`} rel='noopener noreferrer'>
          {item.title}
        </a>
      ),
      navLink: item.navLink
    }

    if (item.children) {
      newItem.children = convertData(item.children, `${key}`)
    }

    return newItem
  })
}
const pemToBuffer = (pem) => {
  const base64 = pem
    .replace(/-----BEGIN (.*)-----/, "")
    .replace(/-----END (.*)-----/, "")
    .replace(/\s/g, "")

  const binaryString = window.atob(base64)
  const bytes = new Uint8Array(binaryString.length)

  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }

  return bytes.buffer
}

const encryptData = async (publicKeyArrayBuffer, data) => {
  try {
    const cryptoKey = await window.crypto.subtle.importKey(
      "spki",
      publicKeyArrayBuffer,
      {
        name: "RSA-OAEP",
        hash: { name: "SHA-256" }
      },
      true,
      ["encrypt"]
    )

    const encryptedArrayBuffer = await window.crypto.subtle.encrypt(
      {
        name: "RSA-OAEP"
      },
      cryptoKey,
      new TextEncoder().encode(data)
    )

    return { success: true, encData: encryptedArrayBuffer }
  } catch (error) {
    console.error(error)
  }
  return { success: false }
}

export const arrayBufferToBase64 = (arrayBuffer) => {
  const binary = []
  const bytes = new Uint8Array(arrayBuffer)
  bytes.forEach((byte) => {
    binary.push(String.fromCharCode(byte))
  })
  return btoa(binary.join(""))
}
export const encryptDataUsingPublicKey = async (publicKey, data) => {
  const stringifiedData = JSON.stringify(data)
  const publicKeyArrayBuffer = pemToBuffer(publicKey)
  const { success, encData } = await encryptData(publicKeyArrayBuffer, stringifiedData)
  if (!success) return { success: false }
  const stringData = arrayBufferToBase64(encData)
  return { success: true, data: stringData }
}

export const encryptedGETParams = async (publicKey, params, excludeFromEncryption = []) => {
  const secureParams = {}
  const otherParams = {}
  const allSecureParams = [
    "site_id",
    "site",
    "command",
    "meter",
    "unique_id",
    "dtr",
    "feeder",
    "substation"
  ]
  //separating normal and secure get params
  Object.keys(params).forEach((currentParamKey) => {
    if (params[currentParamKey] && allSecureParams.includes(currentParamKey) && !excludeFromEncryption.includes(currentParamKey)) {
      secureParams[currentParamKey] = params[currentParamKey]
    } else {
      otherParams[currentParamKey] = params[currentParamKey]
    }
  })

  if (Object.keys(secureParams).length === 0) return params

  //encrypting secure get params
  const encryptedResponse = await encryptDataUsingPublicKey(publicKey, secureParams)
  if (!encryptedResponse.success) {
    toast.error("Failed to encrypted Secure GET params", {
      hideProgressBar: true
    })
    return otherParams
  }

  return { ...otherParams, eData: encodeURIComponent(encryptedResponse.data) }
}

export const caseInsensitiveSort = (rowA, rowB, column) => {


  let a = "--"
  if (rowA[column] === null || rowA[column] === "" || rowA[column] === undefined) {
    a = "--"
  } else {
    if (typeof rowA[column] === "number") {
      a = rowA[column]
    } else {
      a = rowA[column]
        .toString()
        .toLowerCase()
        .replace(/^\s+|\s+$/gm, "")
    }
  }

  let b = "--"
  if (rowB[column] === null || rowB[column] === "" || rowB[column] === undefined) {
    b = "--"
  } else {
    if (typeof rowB[column] === "number") {
      b = rowB[column]
    } else {
      b = rowB[column]
        .toString()
        .toLowerCase()
        .replace(/^\s+|\s+$/gm, "")
    }
  }

  if (a > b) {
    return 1
  }

  if (b > a) {
    return -1
  }

  return 0
}

export const combineKeys = (data) => {
  const combinedObject = {};
  for (const key in data) {
    if (typeof data[key] === 'object' && !Array.isArray(data[key])) {
      Object.assign(combinedObject, combineKeys(data[key]));
    } else {
      combinedObject[key] = data[key];
    }
  }
  return combinedObject;
};

export const nullifyKeysByIndex = (state, startIndex, endIndex) => {
  // Convert the state object to an array of key-value pairs
  const entries = Object.entries(state);

  // Nullify the values for the specified range of entries
  const updatedEntries = entries.map((entry, index) => {
    if (index >= startIndex && index <= endIndex) {
      return [entry[0], null];
    }
    return entry;
  });

  // Convert the updated array of key-value pairs back to an object
  const updatedState = Object.fromEntries(updatedEntries);

  return updatedState;
};

export const getCursorFromUrl = (url) => {
  if (!url) return null; // Handle null or undefined cases

  try {
    // If URL contains "http", it's a full URL; otherwise, treat it as a query string
    const params = new URLSearchParams(url.includes("http") ? new URL(url).search : url);
    return params.get("cursor");
  } catch (error) {
    console.error("Error parsing URL:", error);
    return null;
  }
};


export const colorMapping = {
  BJJ: "#0A3690",
  KAN: "#8AEADC",
  AKT: "#56CCF2",
  MMC: "#B5BFFF",
  UHS: "#3b5ea6",
  jodhpur: "#02C9A8",
  Jaipur: "#ABC7FF",
  BJJhjjnjnj: "#5788F8",
  RMN: "#37AAFE",
  AKT2: "#3C63FF",
  AKTDVHD: "#11ABBE",
  Walaja: "#2F80ED",
  CVK: "#15C9B1",
  JAIPUR: "#00C9FF",
  AKT1: "#5472b1",
  VKI: "#5fbbfe",
  IEW: "#02C9A8",
  TEST: "#73c4fe",
};
export const colors = [
  "#0A3690",
  "#3C63FF",
  "#87cc98",
  "#11ABBE",
  "#ABC7FF",
  "#35d4b9",
  "#3b5ea6",
  "#00C9FF",
  "#4ed9c2",
  "#5472b1",
  "#5fbbfe",
  "#02C9A8",
  "#73c4fe",
  "#22458d",
  "#81e4d4",
  "#37AAFE",
  "#C7EFE8",
  "#39548a",
  "#AEE3EA",
  "#2F80ED",
  "#D7EDF0",
  "#37AAFF",
  "#9dafd3",
  "#09B6A8",
  "#b6c3de",
  "#2cbbcb",
  "#859bc8",
  "#27c4b2",
  "#4d9db2",
  "#73c4b2",
  "#279db2",
  "#5fbbcb",
  "#46a2cb",
  "#219998",
  "#4d9dd8",
  "#46a2e5",
  "#92FE9D",
]

export function getMaxDate({ futureDate, currentDate }) {
  let maxDate = null;

  if (futureDate === false) {
    maxDate = "today";
  }

  if (currentDate === false) {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    // Pick the earlier of today or yesterday
    if (!maxDate || (maxDate === "today" && yesterday < new Date())) {
      maxDate = yesterday;
    }
  }

  return maxDate;
}
